//    Copyright (c) 2025-2026 One Great World LLC. All rights reserved.

import { useState, useEffect } from 'react'
import './Slideshow.css'

interface SlideshowProps {
  slides: React.ReactNode[]
}

function Slideshow({ slides }: SlideshowProps) {
  const [currentSlide, setCurrentSlide] = useState(0)
  const [direction, setDirection] = useState<'forward' | 'backward'>('forward')

  const handleClose = () => {
    // WebView2 support
    if ((window as any).chrome?.webview) {
      (window as any).chrome.webview.postMessage('close')
      return
    }
    window.close()
  }

  const nextSlide = () => {
    if (currentSlide < slides.length - 1) {
      setDirection('forward')
      setCurrentSlide(currentSlide + 1)
    }
  }

  const prevSlide = () => {
    if (currentSlide > 0) {
      setDirection('backward')
      setCurrentSlide(currentSlide - 1)
    }
  }

  const goToSlide = (index: number) => {
    if (index !== currentSlide) {
      setDirection(index > currentSlide ? 'forward' : 'backward')
      setCurrentSlide(index)
    }
  }

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === ' ') {
        e.preventDefault()
        nextSlide()
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault()
        prevSlide()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [currentSlide])

  return (
    <div className="slideshow-container">
      <div className={`slide-wrapper ${direction}`}>
        <div className="slide" key={currentSlide}>
          {slides[currentSlide]}
        </div>
      </div>

      <div className="slide-indicators">
        {slides.map((_, index) => (
          <button
            key={index}
            className={`indicator ${index === currentSlide ? 'active' : ''}`}
            onClick={() => goToSlide(index)}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>

      <div className="controls">
        <button
          className="nav-button prev"
          onClick={prevSlide}
          disabled={currentSlide === 0}
          aria-label="Previous slide"
        >
          Back
        </button>

        <button
          className="nav-button next"
          onClick={nextSlide}
          disabled={currentSlide === slides.length - 1}
          aria-label="Next slide"
        >
          Next →
        </button>

        <button
          className={`nav-button close ${currentSlide === slides.length - 1 ? 'primary' : ''}`}
          onClick={handleClose}
          aria-label="Close tutorial"
        >
          Close
        </button>
      </div>

      <div className="slide-counter">
        {currentSlide + 1} / {slides.length}
      </div>
    </div>
  )
}

export default Slideshow
